import { useCallback } from 'react'
import { getNextUuid, fieldToElementTypeMap } from '@fs/zion-sls'

/**
 * @typedef {Object} UseNamePartsReturn
 * @property {Function} createNewNamePart - Creates a new name part object with default properties.
 * @property {Function} handleNamePartToggle - Handles toggling a name part based on checkbox events.
 * @property {Function} handleRemoveName - Removes a name from the field array, marking it as removed if it existed originally.
 */

/**
 * Custom hook for managing name parts within a form using zion-form.
 * Provides handlers for adding, removing, and toggling name parts, as well as removing entire names from a field array.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {number} params.index - The index of the name in the field array.
 * @param {Object} params.formMethods - Methods from zion-form (e.g., setValue, unregister, getValues).
 * @param {Object} params.fieldArrayMethods - Methods for manipulating the field array (e.g., remove).
 * @returns {UseNamePartsReturn} Handlers for name part and name management.
 */
const useNameParts = ({ index, formMethods, fieldArrayMethods }) => {
  const { setValue, unregister, getValues } = formMethods
  const { remove } = fieldArrayMethods

  const createNewNamePart = useCallback(
    (fieldType) => {
      const elementType = fieldToElementTypeMap[fieldType] // Getting the intermediate element type (e.g., NAME_SURNAME for NAME_SURN)
      const fieldTypesWithSameSuperType = Object.keys(fieldToElementTypeMap).filter(
        (key) => fieldToElementTypeMap[key] === elementType
      ) // Finding all field types that share the same intermediate element type (e.g., NAME_SURN, NAME_GIVEN, NAME_PATR, NAME_MATR for NAME_SURNAME)

      // Check if any existing name parts with the same supertype already exist
      const existingNamePartWithSameSuperType = fieldTypesWithSameSuperType
        .map((fieldTypeWithSameSuperType) => getValues(`names.${index}.${fieldTypeWithSameSuperType}`))
        .find((namePart) => namePart)

      // Reuse existing elementId or create a new one
      const elementId = existingNamePartWithSameSuperType?.elementId || getNextUuid()

      return {
        elementId, // ID for the intermediate element (e.g., NAME_SURNAME)
        id: getNextUuid(), // ID for the field element
        added: true,
        type: fieldType,
        removed: false,
        value: '',
        dispatches: [],
      }
    },
    [getValues, index]
  )

  const addPartToSelectedParts = useCallback(
    (fieldType) => {
      const selectedPartTypes = getValues(`names.${index}.parts`)
      const selectedTypePartsToSet = new Set([...(selectedPartTypes || []), fieldType])

      setValue(`names.${index}.parts`, [...selectedTypePartsToSet], { shouldDirty: true })
    },
    [getValues, index, setValue]
  )

  const removePartFromSelectedParts = useCallback(
    (fieldType) => {
      const selectedPartTypes = getValues(`names.${index}.parts`)
      const removedPartsToSet = selectedPartTypes.filter((part) => part !== fieldType)

      setValue(`names.${index}.parts`, removedPartsToSet, { shouldDirty: true })
    },
    [getValues, index, setValue]
  )

  const addPartToRemovedParts = useCallback(
    (fieldType) => {
      const currentRemovedParts = getValues(`names.${index}.removedParts`) || []

      setValue(`names.${index}.removedParts`, [...currentRemovedParts, fieldType], {
        shouldDirty: true,
      })
    },
    [getValues, index, setValue]
  )

  const removePartFromRemovedParts = useCallback(
    (fieldType) => {
      const currentRemovedParts = getValues(`names.${index}.removedParts`)
      const removedPartsToSet = currentRemovedParts.filter((part) => part !== fieldType)

      setValue(`names.${index}.removedParts`, removedPartsToSet, { shouldDirty: true })
    },
    [getValues, index, setValue]
  )

  const handleAddNamePart = useCallback(
    (fieldType) => {
      const existingNamePart = getValues(`names.${index}.${fieldType}`)
      const namePartToAdd = existingNamePart ? { ...existingNamePart, removed: false } : createNewNamePart(fieldType)

      setValue(`names.${index}.${fieldType}`, namePartToAdd, { shouldDirty: true })

      addPartToSelectedParts(fieldType)
      removePartFromRemovedParts(fieldType)
    },
    [getValues, index, setValue, addPartToSelectedParts, removePartFromRemovedParts, createNewNamePart]
  )

  const handleRemoveExistingNamePart = useCallback(
    (fieldType) => {
      setValue(`names.${index}.${fieldType}.removed`, true, { shouldDirty: true })

      addPartToRemovedParts(fieldType)
      removePartFromSelectedParts(fieldType)
    },
    [index, setValue, addPartToRemovedParts, removePartFromSelectedParts]
  )

  const handleRemoveNewNamePart = useCallback(
    (fieldType) => {
      unregister(`names.${index}.${fieldType}`)

      removePartFromSelectedParts(fieldType)
      removePartFromRemovedParts(fieldType)
    },
    [index, unregister, removePartFromSelectedParts, removePartFromRemovedParts]
  )

  const handleRemoveNamePart = useCallback(
    (fieldType) => {
      const currentNamePart = getValues(`names.${index}.${fieldType}`)
      const isRemovingExisting = currentNamePart && !currentNamePart?.added

      if (isRemovingExisting) {
        handleRemoveExistingNamePart(fieldType)
      } else {
        handleRemoveNewNamePart(fieldType)
      }
    },
    [getValues, index, handleRemoveExistingNamePart, handleRemoveNewNamePart]
  )

  /**
   * Handles toggling of a name part based on a checkbox event.
   * Adds or removes the name part depending on whether the checkbox is checked.
   *
   * @function
   * @param {Object} event - The change event from the checkbox input.
   * @param {boolean} event.target.checked - Indicates if the checkbox is checked.
   * @param {string} fieldType - The type of the name part being toggled.
   */
  const handleNamePartToggle = useCallback(
    ({ target: { checked } }, fieldType) => {
      if (checked) {
        // If checked, we are adding the name part
        handleAddNamePart(fieldType)
      } else {
        // If unchecked, we are removing the name part
        handleRemoveNamePart(fieldType)
      }
    },
    [handleAddNamePart, handleRemoveNamePart]
  )

  /**
   * Removes a name from the form field array at the specified index.
   * If the name was not newly added (i.e., it existed originally), it is marked as removed
   * and added to the `removedNames` array in the form state.
   *
   * @function
   * @param {number} nameIndex - The index of the name to remove from the form field array.
   */
  const handleRemoveName = useCallback(
    (nameIndex) => {
      const [removedNameField, removedNames] = getValues([`names.${nameIndex}`, `removedNames`])
      const { added } = removedNameField || {}

      // If the name was originally there (not newly added), mark it as removed
      if (!added)
        setValue(`removedNames`, [...removedNames, { ...removedNameField, removed: true }], { shouldDirty: true })

      // Remove the name from the form field array
      remove(nameIndex)
    },
    [getValues, remove, setValue]
  )

  return {
    handleNamePartToggle,
    handleRemoveName,
  }
}

export default useNameParts
